import React from "react";
import { Route, Routes as RouterRoutes, Navigate, Outlet, useParams } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import AnonymousLayout from "../layouts/AnonymousLayout";
import Overview from "../pages/Dashboard/Overview";
import Events from "../pages/Dashboard/Events";
import Member from "../pages/member/Member";
// import Profile from "../pages/Profile";
import CreateMember from "../pages/member/CreateMember";
import EditMember from "../pages/member/EditMember";
import PageNotFound from "../pages/PageNotFound";
import StudentDistribution from "../pages/AiAnalytics/StudentDistribution";
// import Hyzine from "../pages/AiAnalytics/Hyzine";
import CameraStatus from "../pages/AiAnalytics/CameraStatus";

import Settings from "../pages/settings/Settings";
import SafetyReports from "../pages/settings/SafetyReports";
import LoginPage from "../pages/auth/Login";
import RegisterPage from "../pages/auth/Register";
import ForgotPasswordPage from "../pages/auth/ForgotPassword";
import ResetPasswordPage from "../pages/auth/ResetPassword";
import AuthCallback from "../pages/auth/AuthCallback";
import ApplicationNotAssigned from "../pages/auth/ApplicationNotAssigned";
import { RequireAssignedApplication } from "../guards/RequireAssignedApplication";
import { APPLICATION_NOT_ASSIGNED_PATH } from "../auth/session/authSession.service";
import { hasAppLoggedOutFlag, redirectToKeycloakLogin, redirectToSsoLogin } from "../auth/logout";
import { isAppSessionActive } from "../auth/session/authGate";
import { isPostLoginGraceActive } from "../auth/session/authSession.grace";
import CloudflareApplicationFrame from "../pages/cloudflare/CloudflareApplicationFrame";
import { AuthTransitionScreen } from "../Components/AuthTransitionScreen";

import {
  CmmZone,
  CmmPTZ,
  Cameras,
  CreateCamera,
  Locations,
  GpuDetails,
  NvrLive,
  NvrRecording,
  NvrClip,
  VideoAgent,
  FeatureRouteGuard,
} from "@variphigen/shared-dashboards";
import Forbidden from "../pages/Forbidden";
import {
  EVENTS_PAGE_PATH,
  AUTH_CALLBACK_PATH,
  MEMBER_PAGE_PATH,
  MEMBER_CREATE_PATH,
  memberEditPath,
} from "./paths";

function LegacyMemberEditRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? memberEditPath(id) : MEMBER_PAGE_PATH} replace />;
}

const isViewerUser = (): boolean => {
  try {
    const authUser = JSON.parse(localStorage.getItem("auth_user") ?? "{}");
    const roles = Array.isArray(authUser?.roles)
      ? authUser.roles.map((r: unknown) => String(r).toUpperCase())
      : [];
    const explicitRole = String(
      authUser?.role ?? authUser?.userType ?? authUser?.user_type ?? ""
    ).toUpperCase();

    return roles.includes("VIEWER") || explicitRole === "VIEWER";
  } catch {
    return false;
  }
};

const MemberRouteGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  return isViewerUser() ? <Navigate to="/" replace /> : children;
};

function LoggedOutRedirect() {
  React.useEffect(() => {
    void redirectToKeycloakLogin();
  }, []);
  return <AuthTransitionScreen />;
}

function SsoLoginRedirect() {
  React.useEffect(() => {
    void redirectToSsoLogin();
  }, []);
  return <AuthTransitionScreen />;
}

function RequireAuth() {
  if (hasAppLoggedOutFlag()) {
    return <LoggedOutRedirect />;
  }

  if (isPostLoginGraceActive()) {
    return <Outlet />;
  }

  if (!isAppSessionActive()) {
    return <SsoLoginRedirect />;
  }

  return <Outlet />;
}

export const AppRoutes: React.FC = () => {
  return (
    <RouterRoutes>
      <Route path={AUTH_CALLBACK_PATH} element={<AuthCallback />} />
      <Route path="/app">
        <Route path="auth/callback" element={<AuthCallback />} />
      </Route>
      <Route
        path="/auth/callback"
        element={<Navigate to={AUTH_CALLBACK_PATH} replace />}
      />
      <Route path={APPLICATION_NOT_ASSIGNED_PATH} element={<ApplicationNotAssigned />} />

      <Route element={<AnonymousLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<PageNotFound />} />
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<RequireAssignedApplication />}>
          <Route element={<MainLayout />}>
          <Route path="/" element={<Overview />} />
          <Route path="/overview" element={<Navigate to="/" replace />} />
          <Route path={EVENTS_PAGE_PATH} element={<Events />} />
          <Route path="/events" element={<Navigate to={EVENTS_PAGE_PATH} replace />} />
          <Route
            path={MEMBER_PAGE_PATH}
            element={
              <MemberRouteGuard>
                <Member />
              </MemberRouteGuard>
            }
          />
          <Route path="/member" element={<Navigate to={MEMBER_PAGE_PATH} replace />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/safety-reports" element={<SafetyReports />} />
          <Route
            path={MEMBER_CREATE_PATH}
            element={
              <MemberRouteGuard>
                <CreateMember />
              </MemberRouteGuard>
            }
          />
          <Route
            path="/member/create"
            element={<Navigate to={MEMBER_CREATE_PATH} replace />}
          />
          <Route
            path="/dashboard/member/edit/:id"
            element={
              <MemberRouteGuard>
                <EditMember />
              </MemberRouteGuard>
            }
          />
          <Route path="/member/edit/:id" element={<LegacyMemberEditRedirect />} />
          {/* <Route path="/profile" element={<Profile />} /> */}
          <Route path="/cmm/zone" element={<FeatureRouteGuard module="cmm" feature="configure-roi"><CmmZone /></FeatureRouteGuard>} />
          <Route path="/cmm/ptz" element={<FeatureRouteGuard module="cmm" feature="ptz-control"><CmmPTZ /></FeatureRouteGuard>} />
          <Route path="/cmm/camera" element={<FeatureRouteGuard module="cmm" feature="camera"><Cameras /></FeatureRouteGuard>} />
          <Route path="/cmm/create-camera" element={<FeatureRouteGuard module="cmm" feature="camera"><CreateCamera /></FeatureRouteGuard>} />
          <Route path="/cmm/locations" element={<FeatureRouteGuard module="cmm" feature="locations"><Locations /></FeatureRouteGuard>} />
          <Route path="/cmm/gpu" element={<FeatureRouteGuard module="cmm" feature="gpus"><GpuDetails /></FeatureRouteGuard>} />

          <Route path="/nvr-live" element={<FeatureRouteGuard module="vms" feature="live-streaming"><NvrLive /></FeatureRouteGuard>} />
          <Route path="/nvr-recording" element={<FeatureRouteGuard module="vms" feature="recordings"><NvrRecording /></FeatureRouteGuard>} />
          <Route path="/nvr-clip" element={<FeatureRouteGuard module="vms" feature="find-clip"><NvrClip /></FeatureRouteGuard>} />
          <Route path="/video-agent" element={<FeatureRouteGuard module="agent" feature="video"><VideoAgent /></FeatureRouteGuard>} />
          <Route path="/403" element={<Forbidden />} />
          <Route
            path="/ai-analytics/student-distribution"
            element={
              <FeatureRouteGuard module="ai-analytics" feature="distribution">
                <StudentDistribution />
              </FeatureRouteGuard>
            }
          />
          {/* <Route path="/ai-analytics/hyzine" element={<Hyzine />} /> */}

          <Route path="/ai-status" element={<CameraStatus />} />
          {/* Prefix avoids shadowing first-class routes like /video-agent (/:appEmbed would match them). */}
          <Route path="/embed/:appEmbed" element={<CloudflareApplicationFrame />} />
          </Route>
        </Route>
      </Route>
    </RouterRoutes>
  );
};
